import { DIMENSIONES_NIVEL, DIMENSION_LABELS } from '@cessi/shared';

type DimNivel = (typeof DIMENSIONES_NIVEL)[number];

const NIVELES = ['1', '2', '3', '4'] as const;
const COLOR_NIVEL: Record<string, string> = {
  '1': 'var(--co-n1)',
  '2': 'var(--co-n2)',
  '3': 'var(--co-n3)',
  '4': 'var(--co-n4)',
};

const CORTES_META: Record<string, { titulo: string; fuente: string }> = {
  p0_1: { titulo: 'Modelo de negocio', fuente: 'P0.1' },
  p0_3: { titulo: 'Tamaño de la organización', fuente: 'P0.3' },
  p0_5: { titulo: 'Geografía', fuente: 'P0.5' },
};

function unaCifra(v: number): string {
  return v.toFixed(2);
}

/** Termómetro de una dimensión + distribución N1–N4 de las canónicas. */
function Dimension({
  code,
  valor,
  distribucion,
}: {
  code: DimNivel;
  valor: number;
  distribucion: Record<string, number> | undefined;
}) {
  const pct = Math.max(0, Math.min(100, (valor / 4) * 100));
  const nombre = DIMENSION_LABELS[code];
  const reparto = NIVELES.map((nivel) => ({ nivel, pct: Number(distribucion?.[nivel] ?? 0) }));
  const hayReparto = reparto.some((r) => r.pct > 0);

  return (
    <div className="mesa-dim">
      <div className="thermo">
        <div className="thermo__head">
          <span className="thermo__label">{nombre}</span>
          <span className="thermo__valor">
            {unaCifra(valor)}
            <span className="mesa-dim__valor-max"> / 4</span>
          </span>
        </div>
        <div
          className="thermo__track"
          role="meter"
          aria-label={`${nombre}: promedio ${unaCifra(valor)} de 4`}
          aria-valuenow={valor}
          aria-valuemin={0}
          aria-valuemax={4}
        >
          <div className="thermo__fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {hayReparto && (
        <>
          <div className="mesa-dim__dist" aria-hidden="true">
            {reparto.map((r) => (
              <span
                key={r.nivel}
                className="mesa-dim__seg"
                style={{ width: `${r.pct}%`, background: COLOR_NIVEL[r.nivel] }}
              />
            ))}
          </div>
          <ul className="mesa-dim__leyenda">
            {reparto.map((r) => (
              <li key={r.nivel}>
                <span className="mesa-punto" style={{ background: COLOR_NIVEL[r.nivel] }} />
                N{r.nivel} {r.pct}%
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function MesaDimensiones({
  promedios,
  pctPorNivel,
}: {
  promedios: Record<string, number>;
  pctPorNivel: Record<string, Record<string, number>>;
}) {
  return (
    <>
      <div className="thermo-list">
        {DIMENSIONES_NIVEL.map((code) => (
          <Dimension
            key={code}
            code={code}
            valor={Number(promedios[code] ?? 0)}
            distribucion={pctPorNivel[code]}
          />
        ))}
      </div>
      <div className="mesa-escala" aria-hidden="true">
        <span>1 · inicial</span>
        <span>2</span>
        <span>3</span>
        <span>4 · consolidado</span>
      </div>
    </>
  );
}

/** Detalle numérico: pct por nivel en tabla mono, no JSON crudo. */
export function MesaDetalleNumerico({
  pctPorNivel,
  cortes,
}: {
  pctPorNivel: Record<string, Record<string, number>>;
  cortes: Record<string, null | Record<string, number>>;
}) {
  return (
    <details className="mesa-details">
      <summary>Detalle numérico (% por nivel y cortes)</summary>

      <table className="mesa-mono-tabla">
        <caption>% de organizaciones por nivel</caption>
        <thead>
          <tr>
            <th scope="col">Dimensión</th>
            {NIVELES.map((nivel) => (
              <th key={nivel} scope="col">
                N{nivel}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DIMENSIONES_NIVEL.map((code) => (
            <tr key={code}>
              <th scope="row">{DIMENSION_LABELS[code]}</th>
              {NIVELES.map((nivel) => (
                <td key={nivel}>{Number(pctPorNivel[code]?.[nivel] ?? 0)}%</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <table className="mesa-mono-tabla">
        <caption>Cortes (n por grupo)</caption>
        <tbody>
          {Object.entries(cortes).map(([clave, grupos]) => (
            <tr key={clave}>
              <th scope="row">{CORTES_META[clave]?.fuente ?? clave}</th>
              <td style={{ textAlign: 'left' }}>
                {grupos
                  ? Object.entries(grupos)
                      .map(([g, n]) => `${g}=${n}`)
                      .join('  ·  ')
                  : 'oculto (k-anonimato)'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/** Cortes agregados con barras; respeta el ocultamiento por k-anonimato. */
export function MesaCortes({
  cortes,
  total,
}: {
  cortes: Record<string, null | Record<string, number>>;
  total: number;
}) {
  const entradas = Object.entries(cortes);

  return (
    <div>
      {entradas.map(([clave, grupos]) => {
        const meta = CORTES_META[clave] ?? { titulo: clave, fuente: clave };
        const filas = grupos ? Object.entries(grupos).sort((a, b) => b[1] - a[1]) : [];
        const maximo = filas.reduce((max, [, n]) => Math.max(max, n), 0) || 1;

        return (
          <section className="mesa-corte" key={clave}>
            <h3 className="mesa-corte__titulo">
              {meta.titulo}
              <span className="mesa-corte__fuente">{meta.fuente}</span>
            </h3>
            {grupos ? (
              <ul className="mesa-corte__lista">
                {filas.map(([grupo, n]) => (
                  <li className="mesa-corte__fila" key={grupo}>
                    <span>{grupo}</span>
                    <span className="mesa-corte__n">
                      {n}
                      {total > 0 && ` · ${Math.round((n / total) * 100)}%`}
                    </span>
                    <span className="mesa-corte__barra">
                      <span
                        className="mesa-corte__relleno"
                        style={{ width: `${(n / maximo) * 100}%` }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mesa-oculto">
                Oculto para proteger el anonimato: algún grupo no llega al mínimo de 5
                organizaciones.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
